import { Alert, Box, Divider, Stack, Typography } from '@mui/material';
import { compact, isUndefined, noop } from 'lodash';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMixpanel } from 'react-mixpanel-browser';

import { useDeviceAlerts } from '@/api/customSensorAlerts/useDeviceAlerts';
import { useDeviceHasSensor } from '@/api/device';
import {
    ProcessingSource,
    SoundPressureBandStatsSample,
    SoundPressureQuietSample,
    SoundPressureSampleStatsDatum,
    useSoundPressureData,
} from '@/api/historicalData';
import { usePreferredTimeZone } from '@/api/userAccount';
import { hydrophoneIcon } from '@/assets';
import { Line, LineChart, LineDataSlice, drawLineChart } from '@/charts';
import { BoxAndWhiskerChart, BoxAndWhiskerLine, drawBoxAndWhiskerChart } from '@/charts/boxAndWhiskerChart';
import { colorsets, linesAcrossSensors } from '@/charts/common';
import { GenericObject, getYValue } from '@/charts/helpers';
import { BoxLimitIcon, LineIcon, WhiskerLimitIcon } from '@/components/data/ColorIcon';
import { Datum, unit, unitType } from '@/components/data/Datum';
import { SensorIcon } from '@/components/data/SensorIcon';
import { FormSelect } from '@/components/form/FormSelect';
import { ChartSensorSelector } from '@/components/spotter/historicalDataTab/ChartLineSelector';
import {
    ChartDataDetails,
    ChartDataSelectors,
    ChartWithInfo,
} from '@/components/spotter/historicalDataTab/ChartWithInfo';
import {
    DataDetailDatum,
    DataDetailsByTimestamp,
} from '@/components/spotter/historicalDataTab/datumDetail/DataDetailPanel';
import {
    DataLineExtent,
    DataLineMean,
    LineChartDataSummary,
} from '@/components/spotter/historicalDataTab/datumDetail/LineChartDataSummary';
import { LineChartDatumDetail } from '@/components/spotter/historicalDataTab/datumDetail/LineChartDatumDetail';
import { CollapsibleSection } from '@/components/ui/CollapsibleSection';
import { useDateRangeContext } from '@/context/DateRangeContext';
import { useSpotterContext } from '@/context/SpotterContext';
import { useTimelineNavigationContext } from '@/context/TimelineNavigationContext';
import { useChartContainer } from '@/hooks/useChartContainer';
import { useChartInteractionTracking } from '@/hooks/useChartInteractionTracking';
import { useElementSize } from '@/hooks/useElementSize';
import { TrackChartViewWhenRendered } from '@/hooks/useIsViewedTracking';
import { useTimedUsage } from '@/hooks/useTimedUsage';
import { useGlobalStore } from '@/store';
import { alertLineParamsForSensorChart, composeAlertLines } from '@/utils';

const splStatsDatumDetailRenderer = <D extends GenericObject>(
    datum: D,
    { color, valueKey, yBarMaxKey, yWhiskerMaxKey, yBarMinKey, yWhiskerMinKey, ...units }: BoxAndWhiskerLine<D>
) => {
    const SplDatum = ({ valueKey }: { valueKey: string }) => (
        <Typography component="span">
            <Datum
                value={getYValue(datum, valueKey)}
                type={unitType(units, datum)}
                sourceUnit={unit(units, datum)}
                hideUnit
            />{' '}
            dB
        </Typography>
    );

    return (
        <Stack>
            <DataDetailDatum label="Median" icon={<LineIcon color={color.line} />}>
                <SplDatum valueKey={valueKey} />
            </DataDetailDatum>
            {!isUndefined(datum[yWhiskerMaxKey]) && (
                <DataDetailDatum label="99th percentile" icon={<WhiskerLimitIcon color={color.line} end="max" />}>
                    <SplDatum valueKey={yWhiskerMaxKey} />
                </DataDetailDatum>
            )}
            <DataDetailDatum
                label="75th percentile"
                icon={<BoxLimitIcon lineColor={color.line} fillColor={color.area} end="max" />}
            >
                <SplDatum valueKey={yBarMaxKey} />
            </DataDetailDatum>
            <DataDetailDatum
                label="25th percentile"
                icon={<BoxLimitIcon lineColor={color.line} fillColor={color.area} end="min" />}
            >
                <SplDatum valueKey={yBarMinKey} />
            </DataDetailDatum>
            {!isUndefined(datum[yWhiskerMinKey]) && (
                <DataDetailDatum label="1st percentile" icon={<WhiskerLimitIcon color={color.line} end="min" />}>
                    <SplDatum valueKey={yWhiskerMinKey} />
                </DataDetailDatum>
            )}
        </Stack>
    );
};

/**
 * Used to indicate "broadband" in the frequency selector
 */
export const FREQ_BROADBAND_ID = 'frequency-broadband';

const LINE_SINGLE_FREQUENCY = {
    id: 'sound-pressure-level-freq',
    label: 'Sound pressure level',
    valueKey: 'median' as const,
    yBarMinKey: 'lower' as const,
    yBarMaxKey: 'upper' as const,
    yWhiskerMinKey: 'min' as const,
    yWhiskerMaxKey: 'max' as const,
    unit: 'dB re 1 µPa²',
    unitType: 'soundPressureLevel',
    dotRenderer: noop,
    datumDetailRenderer: splStatsDatumDetailRenderer as BoxAndWhiskerLine<SingleFrequencyDatum>['datumDetailRenderer'],
    color: colorsets.blue, // placeholder for type; will be replaced per sensor
} satisfies BoxAndWhiskerLine<SingleFrequencyDatum>;

export const BROADBAND_SPL_LINE = {
    id: 'sound-pressure-level-broadband',
    label: 'Sound pressure level',
    valueKey: 'broadbandSpl' as const,
    unit: 'dB re 1 µPa²',
    unitType: 'soundPressureLevel',
    color: colorsets.blue, // placeholder for type; will be replaced per sensor
};

type SingleFrequencyDatum = SoundPressureSampleStatsDatum & {
    sensorPosition: string;
    timestamp: string;
    processing_source: ProcessingSource;
};
export type BroadbandDatum = Omit<SoundPressureBandStatsSample, 'bandStats' | 'isHdr'> | SoundPressureQuietSample;

export type FrequencyBandOption = { id: string; value: string; label: string };

/** Always offered, whatever bands the data turns out to carry, and the selection the chart opens on. */
const BROADBAND_OPTION: FrequencyBandOption = {
    id: FREQ_BROADBAND_ID,
    value: FREQ_BROADBAND_ID,
    label: 'Broadband',
};

/**
 * One option per distinct frequency across the given samples, highest frequency first so the list reads in the
 * same direction as the spectrogram's frequency axis.
 */
export const frequencyBandOptions = (samples: SoundPressureBandStatsSample[]): FrequencyBandOption[] => {
    const bandNumbersByFrequency = new Map<number, number>();
    for (const sample of samples) {
        for (const datum of sample.bandStats) {
            if (!bandNumbersByFrequency.has(datum.frequency)) {
                bandNumbersByFrequency.set(datum.frequency, datum.bandNumber);
            }
        }
    }

    return [...bandNumbersByFrequency.entries()]
        .sort(([aFrequency], [bFrequency]) => bFrequency - aFrequency)
        .map(([frequency, bandNumber]) => ({
            id: `${bandNumber}-${frequency}`,
            value: `${frequency}`,
            label: `${frequency >= 1000 ? `${frequency / 1000} kHz` : `${frequency} Hz`} (Band ${bandNumber})`,
        }));
};

type SplTrackingFrequency = 'broadband' | number;

/**
 * Send `SPL frequency usage` for each period that one frequency is selected. Render it inside the section
 * content, so that it unmounts, and ends the period, when the section collapses.
 */
const TrackSplFrequencyUsage = ({ spotterId, frequency }: { spotterId: string; frequency: SplTrackingFrequency }) => {
    useTimedUsage('SPL frequency usage', { spotterId, frequency });

    return null;
};

export const SoundPressureChart = () => {
    const chartTitle = 'Sound pressure level';
    const mixpanel = useMixpanel();
    const containerId = 'spl-chart' + useId();
    const { spotterId } = useSpotterContext();
    const { hasSensor } = useDeviceHasSensor({ spotterId, type: 'hydrophone' });
    const { dateRange } = useDateRangeContext();
    const isAlertLinesOn = useGlobalStore((state) => state.isAlertLinesOn);
    const isScaleToFitAlertLinesOn = useGlobalStore((state) => state.isScaleToFitAlertLinesOn);
    const { selectedTimestamp, setSelectedTimestamp } = useTimelineNavigationContext();
    const timeZone = usePreferredTimeZone();
    const [selectedData, setSelectedData] = useState<
        LineDataSlice<SingleFrequencyDatum> | LineDataSlice<BroadbandDatum> | null
    >(null);
    const [activeSensorPositions, setActiveSensorPositions] = useState<string[]>([]);
    const [chart, setChart] = useState<BoxAndWhiskerChart<SingleFrequencyDatum> | LineChart<BroadbandDatum>>();

    const {
        data: dataByPosition,
        isFetching,
        isPending,
    } = useSoundPressureData({
        spotterId,
        dateRange,
    });

    const { setContainer, containerRef, containerEl } = useChartContainer();
    const { width, height } = useElementSize(containerRef);

    const { data: deviceAlerts } = useDeviceAlerts({
        config: { enabled: isAlertLinesOn },
    });

    const drawSingleFrequencyChart = useCallback(drawBoxAndWhiskerChart, []);
    const drawBroadbandChart = useCallback(drawLineChart, []);

    const hasData = Object.values(dataByPosition ?? {}).some((data) => data?.length > 0);

    const [sensorPositions, datasetAllFrequencies] = useMemo(() => {
        // flatten all samples with band stats, and collect all sensorPositions that had any
        const sensorPositions: string[] = [];
        const allData: SoundPressureBandStatsSample[] = [];
        Object.entries(dataByPosition ?? {}).forEach(([sensorPosition, positionData]) => {
            const dataWithBandStats = positionData.filter(
                (v) => v.dataQuality === 'present'
            ) as SoundPressureBandStatsSample[]; // weirdly vscode recognizes this narrowing without `as`, but webpack needs it. TS versions out of sync?
            if (dataWithBandStats.length) {
                sensorPositions.push(sensorPosition);
                for (const sample of dataWithBandStats) {
                    allData.push(sample);
                }
            }
        });
        return [sensorPositions, allData];
    }, [dataByPosition]);

    const alertLines = useMemo(
        () =>
            composeAlertLines({
                deviceAlerts,
                triggerTypes: ['smartmooring_threshold_broadband_spl'],
                ...alertLineParamsForSensorChart(sensorPositions, activeSensorPositions),
            }),
        [deviceAlerts, sensorPositions, activeSensorPositions]
    );

    const freqSelectOptions = useMemo(
        () => [
            BROADBAND_OPTION,
            { decoration: <Divider sx={{ m: '2px' }} /> },
            ...frequencyBandOptions(datasetAllFrequencies),
        ],
        [datasetAllFrequencies]
    );

    const {
        control: formControl,
        watch: formWatch,
        reset,
    } = useForm({
        disabled: !freqSelectOptions?.length,
        defaultValues: {
            frequency: '',
        },
        mode: 'onChange',
    });
    const [selectedFrequency] = formWatch(['frequency']);

    useEffect(() => {
        if (freqSelectOptions?.length && !selectedFrequency) {
            reset({ frequency: BROADBAND_OPTION.value });
        }
    }, [freqSelectOptions, selectedFrequency, reset]);

    const broadbandSelected = selectedFrequency === FREQ_BROADBAND_ID;

    const trackingFrequency: SplTrackingFrequency =
        !selectedFrequency || broadbandSelected ? 'broadband' : Number(selectedFrequency);

    const handleUserInteraction = useChartInteractionTracking('Click SPL chart', {
        spotterId,
        frequency: trackingFrequency,
    });

    /*
     * Send `Select SPL frequency` when `selectedFrequency` changes from one frequency to a different one, from the
     * menu or from the arrow keys of `FormSelect`. The first change, from empty to broadband, is the reset above
     * and sends nothing.
     */
    const previousSelectedFrequency = useRef(selectedFrequency);
    useEffect(() => {
        const previous = previousSelectedFrequency.current;
        previousSelectedFrequency.current = selectedFrequency;
        if (previous && selectedFrequency && previous !== selectedFrequency) {
            mixpanel?.track('Select SPL frequency', { spotterId, frequency: trackingFrequency });
        }
    }, [mixpanel, selectedFrequency, spotterId, trackingFrequency]);

    const { broadbandLines, singleFrequencyLines } = useMemo(
        () => ({
            broadbandLines: linesAcrossSensors<Line<BroadbandDatum>>([BROADBAND_SPL_LINE], sensorPositions),
            singleFrequencyLines: linesAcrossSensors<BoxAndWhiskerLine<SingleFrequencyDatum>>(
                [LINE_SINGLE_FREQUENCY],
                sensorPositions
            ),
        }),
        [sensorPositions]
    );

    const selectedFrequencyDataset = useMemo(
        () =>
            broadbandSelected
                ? []
                : compact(
                      datasetAllFrequencies.map(
                          ({ timestamp, sensorPosition, bandStats, processing_source, dataQuality }) => {
                              if (dataQuality !== 'present') return null;

                              const datum = bandStats.find((d) => String(d.frequency) === selectedFrequency);
                              if (!datum) return null;

                              return {
                                  timestamp,
                                  processing_source,
                                  sensorPosition,
                                  ...datum,
                              };
                          }
                      )
                  ),
        [broadbandSelected, datasetAllFrequencies, selectedFrequency]
    );

    const activeLines = useMemo(
        () => ({
            broadband: broadbandLines.filter(
                (l) => !activeSensorPositions.length || activeSensorPositions.includes(l.sensorPosition)
            ),
            singleFrequency: singleFrequencyLines.filter(
                (l) => !activeSensorPositions.length || activeSensorPositions.includes(l.sensorPosition)
            ),
        }),
        [activeSensorPositions, broadbandLines, singleFrequencyLines]
    );

    useEffect(() => {
        if (!containerEl) {
            return;
        }

        if (broadbandSelected) {
            const newChart = drawBroadbandChart<BroadbandDatum, Line<BroadbandDatum>>({
                dataset: datasetAllFrequencies,
                lines: activeLines.broadband,
                xAxisKey: 'timestamp',
                yAxisLabel: 'SPL',
                containerId,
                showAlertLines: isAlertLinesOn,
                scaleChartToFitAllAlerts: isScaleToFitAlertLinesOn,
                alertLines,
                chartWidth: width,
                chartHeight: height,
                timeZone,
                dateRange,
                onSelectData: setSelectedData,
                onSelectTimestamp: setSelectedTimestamp,
                onUserInteraction: handleUserInteraction,
            });
            setChart(newChart);

            return () => newChart.destroy();
        } else {
            const newChart = drawSingleFrequencyChart<SingleFrequencyDatum>({
                dataset: selectedFrequencyDataset,
                lines: activeLines.singleFrequency,
                xAxisKey: 'timestamp',
                yAxisLabel: 'SPL',
                containerId,
                showAlertLines: isAlertLinesOn,
                scaleChartToFitAllAlerts: isScaleToFitAlertLinesOn,
                alertLines,
                chartWidth: width,
                chartHeight: height,
                timeZone,
                dateRange,
                onSelectData: setSelectedData,
                onSelectTimestamp: setSelectedTimestamp,
                onUserInteraction: handleUserInteraction,
            });
            setChart(newChart);

            return () => newChart.destroy();
        }
    }, [
        activeLines.broadband,
        activeLines.singleFrequency,
        alertLines,
        broadbandSelected,
        containerEl,
        containerId,
        datasetAllFrequencies,
        dateRange,
        timeZone,
        drawBroadbandChart,
        drawSingleFrequencyChart,
        height,
        isAlertLinesOn,
        isScaleToFitAlertLinesOn,
        selectedFrequencyDataset,
        width,
        setSelectedTimestamp,
        handleUserInteraction,
    ]);

    useEffect(() => {
        chart?.setSelectedTimestamp?.(selectedTimestamp ? new Date(selectedTimestamp) : null);
    }, [selectedTimestamp, chart]);

    if (!hasData && !hasSensor) {
        return;
    }

    return (
        <CollapsibleSection
            isFetching={isFetching}
            isPending={isPending}
            title={chartTitle}
            icon={<SensorIcon src={hydrophoneIcon} />}
        >
            {hasData ? (
                <>
                    <TrackChartViewWhenRendered
                        containerEl={containerEl}
                        chartTitle={chartTitle}
                        spotterId={spotterId}
                    />
                    {spotterId && <TrackSplFrequencyUsage spotterId={spotterId} frequency={trackingFrequency} />}
                    <ChartWithInfo chart={<Box ref={setContainer} id={containerId} width={1} height={1} />}>
                        <ChartDataSelectors>
                            <ChartSensorSelector
                                sensorPositions={sensorPositions}
                                onChange={setActiveSensorPositions}
                            />
                            {!!freqSelectOptions?.length && (
                                <FormSelect
                                    name="frequency"
                                    label=""
                                    control={formControl}
                                    options={freqSelectOptions}
                                    inputFieldProps={{
                                        id: 'frequency-select',
                                        type: 'text',
                                        size: 'small',
                                    }}
                                />
                            )}
                        </ChartDataSelectors>
                        <ChartDataDetails>
                            {!selectedData && broadbandSelected && (
                                <LineChartDataSummary
                                    dateRange={dateRange}
                                    lines={activeLines.broadband}
                                    dataset={datasetAllFrequencies as BroadbandDatum[]}
                                    renderLineSummary={() => (
                                        <>
                                            <DataLineMean />
                                            <DataLineExtent />
                                        </>
                                    )}
                                />
                            )}
                            {!selectedData && !broadbandSelected && (
                                <LineChartDataSummary
                                    dateRange={dateRange}
                                    lines={activeLines.singleFrequency}
                                    dataset={selectedFrequencyDataset}
                                    renderLineSummary={({ line }) => (
                                        <>
                                            <DataLineMean label="Averaged median SPL" />
                                            <DataLineExtent
                                                minProps={{ valueKey: line.yWhiskerMinKey }}
                                                maxProps={{ valueKey: line.yWhiskerMaxKey }}
                                            />
                                        </>
                                    )}
                                />
                            )}
                            <DataDetailsByTimestamp
                                selectedData={
                                    selectedData as LineDataSlice<SingleFrequencyDatum | BroadbandDatum> | null
                                }
                                datumRenderer={({ lines, datum }) => (
                                    <LineChartDatumDetail
                                        datum={datum}
                                        dataset={broadbandSelected ? datasetAllFrequencies : selectedFrequencyDataset}
                                        lines={lines}
                                    />
                                )}
                            />
                        </ChartDataDetails>
                    </ChartWithInfo>
                </>
            ) : (
                <Alert severity="info">No sound pressure data for selected date range</Alert>
            )}
        </CollapsibleSection>
    );
};
